import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';

const ACCENT = '#f97316';

interface Benchmark {
  id: string;
  metric: string;
  category: string;
  percentile_25: number;
  percentile_50: number;
  percentile_75: number;
  percentile_90: number;
  sample_size: number;
}

interface ConnectorPop {
  connector_id: string;
  connector_name: string;
  install_count: number;
  active_sync_count: number;
  avg_records_per_sync: number;
}

interface ErrorPattern {
  id: string;
  pattern: string;
  connector_id: string;
  occurrence_count: number;
  suggested_fix: string;
  fix_success_rate: number;
}

interface ConnectorRequest {
  connector_name: string;
  total_votes: number;
  voter_count: number;
}

interface MyPosition {
  [metric: string]: {
    value: number;
    percentile_label: string;
    percentile: number;
  };
}

const METRIC_LABELS: Record<string, string> = {
  records_per_sync: 'Records per Sync',
  sync_duration_ms: 'Avg Sync Duration (ms)',
  error_rate_percent: 'Error Rate (%)',
};

function BenchmarkChart({ benchmarks, position }: { benchmarks: Benchmark[]; position: MyPosition | null }) {
  if (benchmarks.length === 0) {
    return <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No benchmark data available yet. Data is aggregated daily at 3 AM.</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {benchmarks.map(bm => {
        const label = METRIC_LABELS[bm.metric] || bm.metric;
        const max = bm.percentile_90 * 1.2 || 100;
        const myVal = position?.[bm.metric]?.value ?? null;
        const bars = [
          { label: 'P25', value: bm.percentile_25, color: '#dbeafe' },
          { label: 'P50', value: bm.percentile_50, color: '#93c5fd' },
          { label: 'P75', value: bm.percentile_75, color: '#3b82f6' },
          { label: 'P90', value: bm.percentile_90, color: '#1d4ed8' },
        ];

        return (
          <div key={bm.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{label}</span>
              <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{bm.sample_size} samples</span>
            </div>
            <svg width="100%" height="80" viewBox="0 0 600 80" style={{ overflow: 'visible' }}>
              {/* Background */}
              <rect x="0" y="20" width="600" height="30" rx="4" fill="#f3f4f6" />
              {/* Percentile bars */}
              {bars.map((bar, i) => (
                <g key={i}>
                  <rect
                    x="0"
                    y="20"
                    width={Math.max(4, (bar.value / max) * 600)}
                    height="30"
                    rx="4"
                    fill={bar.color}
                    opacity={0.7}
                  />
                  <text
                    x={Math.max(4, (bar.value / max) * 600) + 4}
                    y="16"
                    fontSize="10"
                    fill="#6b7280"
                  >
                    {bar.label}: {Math.round(bar.value).toLocaleString()}
                  </text>
                </g>
              ))}
              {/* Your position marker */}
              {myVal !== null && (
                <g>
                  <line
                    x1={Math.min(596, (myVal / max) * 600)}
                    y1="14"
                    x2={Math.min(596, (myVal / max) * 600)}
                    y2="56"
                    stroke={ACCENT}
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  <text
                    x={Math.min(580, (myVal / max) * 600) + 6}
                    y="68"
                    fontSize="11"
                    fontWeight="700"
                    fill={ACCENT}
                  >
                    You: {Math.round(myVal).toLocaleString()}
                  </text>
                </g>
              )}
            </svg>
          </div>
        );
      })}
    </div>
  );
}

function RankingBadge({ position }: { position: MyPosition | null }) {
  if (!position || Object.keys(position).length === 0) {
    return <p style={{ color: '#6b7280', fontStyle: 'italic' }}>Complete your first sync to see your ranking.</p>;
  }

  // Find the best percentile across all metrics
  const percentiles = Object.values(position).map(p => p.percentile);
  const bestPct = Math.min(...percentiles);
  const bestLabel = bestPct <= 10 ? 'Top 10%' : bestPct <= 25 ? 'Top 25%' : bestPct <= 50 ? 'Top 50%' : bestPct <= 75 ? 'Top 75%' : 'Getting Started';
  const badgeColor = bestPct <= 10 ? '#059669' : bestPct <= 25 ? '#2563eb' : bestPct <= 50 ? '#7c3aed' : '#6b7280';

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 120,
        height: 120,
        borderRadius: '50%',
        border: `4px solid ${badgeColor}`,
        marginBottom: '1rem',
      }}>
        <span style={{ fontSize: '1.25rem', fontWeight: 700, color: badgeColor }}>{bestLabel}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
        {Object.entries(position).map(([metric, pos]) => (
          <div key={metric} style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '0.5rem 0.75rem',
            backgroundColor: '#f9fafb',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
          }}>
            <span style={{ color: '#374151' }}>{METRIC_LABELS[metric] || metric}</span>
            <span style={{
              fontWeight: 600,
              color: pos.percentile <= 25 ? '#059669' : pos.percentile <= 50 ? '#2563eb' : '#6b7280',
            }}>
              {pos.percentile_label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function IntelligencePage() {
  const [benchmarks, setBenchmarks] = useState<Benchmark[]>([]);
  const [connectors, setConnectors] = useState<ConnectorPop[]>([]);
  const [errorPatterns, setErrorPatterns] = useState<ErrorPattern[]>([]);
  const [requests, setRequests] = useState<ConnectorRequest[]>([]);
  const [position, setPosition] = useState<MyPosition | null>(null);
  const [expandedError, setExpandedError] = useState<string | null>(null);
  const [newConnector, setNewConnector] = useState('');
  const [voting, setVoting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [bmRes, connRes, errRes, reqRes, posRes] = await Promise.all([
        api.get<{ benchmarks: Benchmark[] }>('/intelligence/benchmarks'),
        api.get<{ connectors: ConnectorPop[] }>('/intelligence/popular-connectors'),
        api.get<{ patterns: ErrorPattern[] }>('/intelligence/error-patterns'),
        api.get<{ requests: ConnectorRequest[] }>('/intelligence/connector-requests'),
        api.get<{ position: MyPosition | null }>('/intelligence/my-position'),
      ]);
      setBenchmarks(bmRes.benchmarks);
      setConnectors(connRes.connectors);
      setErrorPatterns(errRes.patterns);
      setRequests(reqRes.requests);
      setPosition(posRes.position);
    } catch {
      // API errors handled by UI state
    } finally {
      setLoading(false);
    }
  }

  async function handleVote(connectorName: string) {
    setVoting(true);
    try {
      await api.post('/intelligence/vote-connector', { connector_name: connectorName });
      const reqRes = await api.get<{ requests: ConnectorRequest[] }>('/intelligence/connector-requests');
      setRequests(reqRes.requests);
    } catch {
      // Vote failure handled silently
    } finally {
      setVoting(false);
    }
  }

  async function handleRequestNew() {
    if (!newConnector.trim()) return;
    setVoting(true);
    try {
      await api.post('/intelligence/vote-connector', { connector_name: newConnector.trim() });
      setNewConnector('');
      const reqRes = await api.get<{ requests: ConnectorRequest[] }>('/intelligence/connector-requests');
      setRequests(reqRes.requests);
    } catch {
      // Request failure handled silently
    } finally {
      setVoting(false);
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4rem' }}>
        <p style={{ color: '#6b7280' }}>Loading intelligence data...</p>
      </div>
    );
  }

  const maxInstalls = Math.max(1, ...connectors.map(c => c.install_count));

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Cross-Client Intelligence</h1>
        <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>
          Anonymous benchmarks and insights from the Bridge community
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Benchmarks Panel */}
          <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.25rem' }}>&#x1F4CA;</span> Community Benchmarks
            </h2>
            <BenchmarkChart benchmarks={benchmarks} position={position} />
          </div>

          {/* Popular Connectors */}
          <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 1rem' }}>Popular Connectors</h2>
            {connectors.length === 0 ? (
              <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No connector data yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {connectors.map((c, i) => (
                  <div key={c.connector_id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af', width: 24, textAlign: 'right' }}>#{i + 1}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                        <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{c.connector_name}</span>
                        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {c.install_count} installs &middot; {c.active_sync_count} active
                        </span>
                      </div>
                      <div style={{ height: 6, backgroundColor: '#f3f4f6', borderRadius: 3 }}>
                        <div style={{
                          height: 6,
                          backgroundColor: ACCENT,
                          borderRadius: 3,
                          width: `${(c.install_count / maxInstalls) * 100}%`,
                          transition: 'width 0.3s',
                        }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Error Patterns */}
          <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 1rem' }}>Common Error Patterns</h2>
            {errorPatterns.length === 0 ? (
              <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No error patterns detected yet.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem', color: '#6b7280', fontWeight: 500 }}>Pattern</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem', color: '#6b7280', fontWeight: 500 }}>Connector</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem', color: '#6b7280', fontWeight: 500 }}>Count</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem', color: '#6b7280', fontWeight: 500 }}>Fix Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {errorPatterns.slice(0, 20).map(ep => (
                    <React.Fragment key={ep.id}>
                      <tr
                        onClick={() => setExpandedError(expandedError === ep.id ? null : ep.id)}
                        style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                      >
                        <td style={{ padding: '0.5rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ep.pattern}
                        </td>
                        <td style={{ padding: '0.5rem', color: '#6b7280' }}>{ep.connector_id}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>{ep.occurrence_count}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                          <span style={{
                            padding: '0.125rem 0.375rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            backgroundColor: ep.fix_success_rate >= 70 ? '#d1fae5' : ep.fix_success_rate >= 30 ? '#fef3c7' : '#fee2e2',
                            color: ep.fix_success_rate >= 70 ? '#065f46' : ep.fix_success_rate >= 30 ? '#92400e' : '#991b1b',
                          }}>
                            {ep.fix_success_rate}%
                          </span>
                        </td>
                      </tr>
                      {expandedError === ep.id && (
                        <tr>
                          <td colSpan={4} style={{ padding: '0.75rem 0.5rem', backgroundColor: '#f9fafb' }}>
                            <div style={{ fontSize: '0.8125rem' }}>
                              <strong>Suggested Fix:</strong>{' '}
                              <span style={{ color: '#374151' }}>{ep.suggested_fix}</span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Your Ranking */}
          <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 1rem' }}>Your Ranking</h2>
            <RankingBadge position={position} />
          </div>

          {/* Connector Wishlist */}
          <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 1rem' }}>Connector Wishlist</h2>
            {requests.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                {requests.map(r => (
                  <div key={r.connector_name} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '0.375rem',
                  }}>
                    <div>
                      <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{r.connector_name}</span>
                      <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.5rem' }}>
                        {r.total_votes} vote{r.total_votes !== 1 ? 's' : ''} from {r.voter_count} MSP{r.voter_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <button
                      onClick={() => handleVote(r.connector_name)}
                      disabled={voting}
                      style={{
                        padding: '0.25rem 0.625rem',
                        backgroundColor: ACCENT,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        cursor: voting ? 'not-allowed' : 'pointer',
                        opacity: voting ? 0.6 : 1,
                      }}
                    >
                      Vote
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Request New */}
            <div style={{ borderTop: requests.length > 0 ? '1px solid #e5e7eb' : 'none', paddingTop: requests.length > 0 ? '0.75rem' : 0 }}>
              <label style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#374151', display: 'block', marginBottom: '0.375rem' }}>
                Request a New Connector
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={newConnector}
                  onChange={e => setNewConnector(e.target.value)}
                  placeholder="e.g., HaloPSA, SyncroMSP"
                  onKeyDown={e => e.key === 'Enter' && handleRequestNew()}
                  style={{
                    flex: 1,
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    outline: 'none',
                  }}
                />
                <button
                  onClick={handleRequestNew}
                  disabled={voting || !newConnector.trim()}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: ACCENT,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: voting || !newConnector.trim() ? 'not-allowed' : 'pointer',
                    opacity: voting || !newConnector.trim() ? 0.6 : 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Request
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
